import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getGroupMembers, getUsers, addGroupMember, removeGroupMember } from '../../services/iam';
import { toast } from '@/lib/toast';
import { X, Users, UserPlus, Plus } from 'lucide-react';
import { Button, IconButton, LoadingRegion, Modal, SearchField } from '@/components/ds-zta';
import { useDialogs } from '@/components/dialogs/DialogProvider';
import { useDebouncedValue } from '@/lib/useDebouncedValue';

// Design eval F9: getUsers() with no limit returned only the backend's default
// page, so the picker silently missed everyone after it. The picker now asks
// the server (`q`, `limit`) and says when there are more matches than it shows.
const USER_PICKER_LIMIT = 50;
const MEMBER_LIMIT = 500;

interface Props {
  groupId: string;
  groupName: string;
  onClose: () => void;
}

const ManageGroupMembersModal: React.FC<Props> = ({ groupId, groupName, onClose }) => {
  const queryClient = useQueryClient();
  const { confirm: confirmDialog } = useDialogs();

  const [searchTerm, setSearchTerm] = useState('');
  const q = useDebouncedValue(searchTerm.trim());

  const { data: membersData, isLoading: isLoadingMembers } = useQuery(['groupMembers', groupId], () => getGroupMembers(groupId, { limit: MEMBER_LIMIT, offset: 0 }));
  const { data: usersData, isLoading: isLoadingUsers, isFetching: isFetchingUsers } = useQuery(
    ['users', 'picker', q],
    () => getUsers({ q: q || undefined, limit: USER_PICKER_LIMIT, offset: 0 }),
    { keepPreviousData: true }
  );

  const addMemberMutation = useMutation((userId: string) => addGroupMember(groupId, userId), {
    onSuccess: () => {
      queryClient.invalidateQueries(['groupMembers', groupId]);
      toast.success('Member added');
    },
    onError: (err: any) => {
      toast.error(`Failed to add member: ${err.message}`);
    },
  });

  const removeMemberMutation = useMutation((userId: string) => removeGroupMember(groupId, userId), {
    onSuccess: () => {
      queryClient.invalidateQueries(['groupMembers', groupId]);
      toast.success('Member removed');
    },
    onError: (err: any) => {
      toast.error(`Failed to remove member: ${err.message}`);
    },
  });

  const isLoading = isLoadingMembers || isLoadingUsers;
  const members = membersData?.data || [];
  const allUsers = usersData?.data || [];
  const membersTotal = Math.max(membersData?.total || 0, members.length);
  const usersTotal = Math.max(usersData?.total || 0, allUsers.length);

  const memberIds = new Set(members.map(m => m.id));
  const usersNotInGroup = allUsers.filter(u => !memberIds.has(u.id));

  // The server already matched `q`; while a new search is in flight, keep
  // narrowing the previous result locally so the list follows the typing.
  const filteredAvailableUsers = usersNotInGroup.filter(user =>
    (user.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredMembers = members.filter(member =>
    (member.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (member.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Modal
      open
      onClose={onClose}
      title={`Manage Members (${groupName})`}
      size="xl"
      footer={
        <>
          <span className="mr-auto self-center text-sm text-[var(--d2d-text-secondary)]">
            <span className="font-medium text-[var(--d2d-text-primary)]">{membersTotal}</span> total {membersTotal === 1 ? 'member' : 'members'}
          </span>
          <Button onClick={onClose}>Done</Button>
        </>
      }
    >
        {/* Search Bar */}
        <div className="pb-4 mb-4 border-b border-[var(--d2d-border)]">
          <SearchField
            label="Search users"
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={setSearchTerm}
            help={
              usersTotal > allUsers.length
                ? `Showing ${allUsers.length} of ${usersTotal} users. Refine your search to find others.`
                : undefined
            }
          />
        </div>

        {/* Content */}
        <div>
          {isLoading ? (
            <LoadingRegion lines={8} label="Loading members" className="py-4" />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Available Users */}
              <div className="flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-[var(--d2d-text-secondary)] uppercase tracking-wide">
                    Available Users
                  </h4>
                  <span className="text-xs text-[var(--d2d-text-muted)] bg-[var(--ds-color-bg-subtle)] px-2 py-1 rounded-full" aria-busy={isFetchingUsers || undefined}>
                    {filteredAvailableUsers.length}
                  </span>
                </div>
                <div className="flex-1 h-96 overflow-y-auto border-2 border-[var(--d2d-border)] rounded-lg bg-[var(--d2d-bg-secondary)]">
                  {filteredAvailableUsers.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center p-4">
                      <UserPlus className="h-12 w-12 text-[var(--d2d-text-muted)] mb-2" />
                      <p className="text-sm text-[var(--d2d-text-muted)]">
                        {searchTerm ? 'No users found' : 'All users are already members'}
                      </p>
                    </div>
                  ) : (
                    <div className="p-2 space-y-1">
                      {filteredAvailableUsers.map(user => (
                        <div 
                          key={user.id} 
                          className="flex items-center justify-between p-3 rounded-lg hover:bg-[var(--ds-color-bg-hover)] transition-colors group"
                        >
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            <div className="flex-shrink-0 w-8 h-8 bg-[var(--accent-tag-bg)] rounded-full flex items-center justify-center text-[var(--accent-tag-text)] font-semibold text-sm">
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[var(--d2d-text-primary)] truncate">
                                {user.name}
                              </p>
                              <p className="text-xs text-[var(--d2d-text-muted)] truncate">
                                {user.email}
                              </p>
                            </div>
                          </div>
                          <IconButton
                            aria-label={`Add ${user.name} to group`}
                            icon={<Plus />}
                            size="sm"
                            onClick={() => addMemberMutation.mutate(user.id)}
                            disabled={addMemberMutation.isLoading}
                            className="flex-shrink-0 ml-3 text-[var(--d2d-success)]"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Current Members */}
              <div className="flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-[var(--d2d-text-secondary)] uppercase tracking-wide">
                    Current Members
                  </h4>
                  <span className="text-xs bg-[var(--ds-color-bg-subtle)] text-[var(--d2d-text-primary)] px-2 py-1 rounded-full">
                    {filteredMembers.length}
                  </span>
                </div>
                {membersTotal > members.length && (
                  <p className="text-xs text-[var(--d2d-text-muted)] mb-2">
                    Showing the first {members.length} of {membersTotal} members.
                  </p>
                )}
                <div className="flex-1 h-96 overflow-y-auto border-2 border-[var(--d2d-border)] rounded-lg bg-[var(--selection-tint)]">
                  {filteredMembers.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center p-4">
                      <Users className="h-12 w-12 text-[var(--d2d-text-muted)] mb-2" />
                      <p className="text-sm text-[var(--d2d-text-muted)]">
                        {searchTerm ? 'No members found' : 'No members yet'}
                      </p>
                    </div>
                  ) : (
                    <div className="p-2 space-y-1">
                      {filteredMembers.map(member => (
                        <div 
                          key={member.id} 
                          className="flex items-center justify-between p-3 rounded-lg hover:bg-[var(--ds-color-bg-hover)] transition-colors group"
                        >
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            <div className="flex-shrink-0 w-8 h-8 bg-[var(--zta-tint-info)] rounded-full flex items-center justify-center text-[var(--d2d-info)] font-semibold text-sm">
                              {member.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[var(--d2d-text-primary)] truncate">
                                {member.name}
                              </p>
                              <p className="text-xs text-[var(--d2d-text-muted)] truncate">
                                {member.email}
                              </p>
                            </div>
                          </div>
                          <IconButton
                            aria-label={`Remove ${member.name} from group`}
                            icon={<X />}
                            size="sm"
                            onClick={async () => {
                              if (await confirmDialog({
                                title: `Remove ${member.name} from ${groupName}?`,
                                message: `${member.name} (${member.email}) loses any access granted through the group "${groupName}".`,
                                confirmLabel: 'Remove',
                                variant: 'danger',
                              })) {
                                removeMemberMutation.mutate(member.id);
                              }
                            }}
                            disabled={removeMemberMutation.isLoading}
                            className="flex-shrink-0 ml-3 text-[var(--danger-text)]"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

    </Modal>
  );
};

export default ManageGroupMembersModal;
